import { HelpCircle, Settings, UserRoundPen } from 'lucide-react';
import { NavigationBar } from 'pixelize-design-library';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { UserRole } from '@/types/user';
import { NotificationBell } from '@modules/notifications/NotificationBell';
import { NotificationCenter } from '@modules/notifications/NotificationCenter';
import { LanguageSelect } from '@core/i18n/LanguageSelect';
import { ThemeToggle } from '@core/Theme/ThemeToggle';
import { useAuthentication } from '@core/auth/useAuthentication';
import { uploadUrl } from '@core/store/Api';
import { useGetMeQuery } from '@core/store/Auth/AuthService';
import { useAppSelector } from '@core/store/hooks';
import { Box, HStack } from 'pixelize-design-library';

interface AppNavBarProps {
  onSidebarToggle: () => void;
  isSidebarOpen: boolean;
  forceSidebarToggle?: boolean;
}

function profilePathForRole(role: UserRole | undefined): string | null {
  if (role === 'lawyer') return '/lawyer/profile';
  return '/account';
}

// NOTE: /api/user/notifications is guarded to role 'user'; staff have their own pages.
function staffNotificationsPath(role: UserRole | undefined): string | null {
  if (role === 'admin') return '/admin/notifications';
  if (role === 'lawyer') return '/lawyer/notifications';
  return null;
}

export function AppNavBar({ onSidebarToggle, isSidebarOpen, forceSidebarToggle }: AppNavBarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.auth.user);
  const { handleLogout } = useAuthentication();
  // NOTE: refreshes the photo paths of a session saved before they existed.
  useGetMeQuery(undefined, { skip: !user });

  const profilePath = profilePathForRole(user?.role);
  const staffBellPath = staffNotificationsPath(user?.role);

  const navMenu = profilePath
    ? [
        { title: t('nav.profile'), url: profilePath, icon: <UserRoundPen size={20} /> },
        { title: t('nav.settings'), url: profilePath, icon: <Settings size={20} /> },
        { title: t('nav.help'), url: profilePath, icon: <HelpCircle size={20} /> }
      ]
    : [];

  return (
    <Box data-testid='app-navbar'>
      <NavigationBar
        navMenu={navMenu}
        userAvathar={user?.avatarPath ? uploadUrl(user.avatarPath) : undefined}
        userName={user?.name ?? 'User'}
        moreIcon={
          <HStack spacing={2}>
            <LanguageSelect />
            <Box>
              <ThemeToggle />
            </Box>
            {staffBellPath ? (
              <NotificationBell needsYou={0} unread={0} onClick={() => navigate(staffBellPath)} />
            ) : (
              <NotificationCenter />
            )}
          </HStack>
        }
        handleNavOnClick={(url) => navigate(url)}
        handleLogout={handleLogout}
        logoutText={t('nav.signOut')}
        onSideBarToggole={onSidebarToggle}
        sideBarToggole={isSidebarOpen}
        forceSideBarToggle={forceSidebarToggle}
      />
    </Box>
  );
}

export default AppNavBar;
